/**
 * shared/types/canonical_techpack_fields.ts
 *
 * THE single definition of the canonical Tech Pack panel surface, shared by
 * SpecFlow and Studio. Mirrors specflow/core/canonical_techpack.py, which is
 * the authoritative Python model behind
 * governance/product/CANONICAL_TECH_PACK_VERSION_CONTRACT.md §1.
 *
 * WHY THIS FILE EXISTS. SpecFlow and Studio each grew their own BOM row
 * shape, their own measurement key list, and their own image slot names. That
 * divergence is exactly what the canonical contract exists to end. Any panel
 * control in EITHER product binds to a `canonicalPath` from this file --
 * never to a product-local field name. If a control cannot name its canonical
 * path, it does not belong in the panel.
 *
 * DRIFT RULE: this file and canonical_techpack.py must agree. The Python side
 * is authoritative; this is the TypeScript projection of it.
 * specflow/test_canonical_panel_parity.py fails if they diverge.
 *
 * NOTHING HERE FABRICATES A VALUE. Every field renders blank until a human or
 * an explicit extraction supplies one -- contract §1: "this contract never
 * fabricates a price, cost, vendor, or consumption figure that wasn't
 * explicitly entered."
 */

// ---------------------------------------------------------------------------
// Measurements -- 14 canonical keys (contract §1, single-size)
// ---------------------------------------------------------------------------

export const CANONICAL_MEASUREMENT_KEYS = [
  "front_length", "back_length", "shoulder", "chest", "waist", "hip",
  "hem", "sleeve_length", "sleeve_width", "cuff", "collar_width",
  "waist_height", "front_rise", "back_rise",
] as const;

export type CanonicalMeasurementKey = typeof CANONICAL_MEASUREMENT_KEYS[number];

/**
 * Garment-aware relevance, ported from SpecFlow's
 * filterMeasurementsByCategory(). Studio has no equivalent today.
 *
 * CRITICAL SEMANTICS, preserved exactly from SpecFlow: irrelevant fields are
 * DIMMED, never cleared and never removed. Switching garment category must be
 * non-destructive -- a value already entered survives the switch. Hiding the
 * field would destroy data the user cannot see to recover.
 */
export const TOPS_ONLY_MEASUREMENTS: CanonicalMeasurementKey[] = [
  "shoulder", "sleeve_length", "sleeve_width", "cuff", "collar_width",
];

export const BOTTOMS_ONLY_MEASUREMENTS: CanonicalMeasurementKey[] = [
  "front_rise", "back_rise", "waist_height",
];

/** Which measurement keys to dim for a garment category. Unknown/"both"/
 *  "children" dims nothing -- show everything rather than guess. */
export function dimmedMeasurementKeys(category?: string | null): CanonicalMeasurementKey[] {
  const c = (category ?? "").trim().toLowerCase();
  if (c === "tops") return BOTTOMS_ONLY_MEASUREMENTS;
  if (c === "bottoms") return TOPS_ONLY_MEASUREMENTS;
  return [];
}

/** Garment-aware display labels, matching export.js's PDF_MEAS_FIELDS
 *  overrides and canonical_renderer.py's measurement_label(). Presentation
 *  only -- the canonical KEY never changes. */
const BASE_MEASUREMENT_LABELS: Record<CanonicalMeasurementKey, string> = {
  front_length: "Front Length", back_length: "Back Length", shoulder: "Shoulder",
  chest: "Chest", waist: "Waist", hip: "Hip", hem: "Hem",
  sleeve_length: "Sleeve Length", sleeve_width: "Sleeve Width", cuff: "Cuff",
  collar_width: "Collar Width", waist_height: "Waist Height",
  front_rise: "Front Rise", back_rise: "Back Rise",
};

const GARMENT_LABEL_OVERRIDES: Record<string, Partial<Record<CanonicalMeasurementKey, string>>> = {
  bottoms: { hem: "Bottom Hem", waist_height: "Rise Height" },
  skirts:  { hem: "Bottom Hem" },
};

export function measurementLabel(
  key: CanonicalMeasurementKey, garmentType?: string | null,
): string {
  const g = (garmentType ?? "").trim().toLowerCase();
  for (const [family, overrides] of Object.entries(GARMENT_LABEL_OVERRIDES)) {
    if (g.includes(family) && overrides[key]) return overrides[key]!;
  }
  return BASE_MEASUREMENT_LABELS[key];
}

/**
 * Studio labels its measurement rows with a diagram letter prefix
 * (`A_front_length`, `D_chest`) because the UI shows those letters against a
 * technical drawing. Canonical keys carry no prefix.
 *
 * Mirrors studio_measurement_key_to_canonical() in
 * specflow/core/canonical_mapping.py. Without this mapping the resolver
 * dropped EVERY Studio measurement, so a Studio-originated locked Version
 * snapshot contained none of them.
 *
 * Returns null for Studio's six extra garment measurements (O_inseam,
 * P_side_seam, Q/R skirt lengths, S_skirt_hem, T_sleeve_cap), for
 * user-defined `custom_*` rows, and for bookkeeping keys. Those are real
 * data, but adding them to the canonical 14 is a schema change, not a
 * mapping decision.
 */
export function studioMeasurementKey(key: string): CanonicalMeasurementKey | null {
  if (!key) return null;
  if ((CANONICAL_MEASUREMENT_KEYS as readonly string[]).includes(key)) {
    return key as CanonicalMeasurementKey;
  }
  if (key.length > 2 && key[1] === "_" && /[A-Za-z]/.test(key[0])) {
    const stripped = key.slice(2);
    if ((CANONICAL_MEASUREMENT_KEYS as readonly string[]).includes(stripped)) {
      return stripped as CanonicalMeasurementKey;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Materials row -- ONE shape for bom, interlining AND trim (contract §1)
// ---------------------------------------------------------------------------

export type MaterialCellType = "text" | "number" | "currency" | "image";

export interface MaterialColumn {
  /** Canonical field name on the materials row. Also the dot-path suffix:
   *  `bom[0].unit_price`, `trim[2].fabric_code`, etc. */
  field: string;
  label: string;
  type: MaterialCellType;
  /** i18n key where both products already have one; null = new surface. */
  i18nKey: string | null;
  /** Present in Studio's live BOM table today. */
  inStudioToday: boolean;
  /** Present in SpecFlow's live BOM table today. */
  inSpecFlowToday: boolean;
  /** Derived, never hand-entered (see COSTING_RELATIONSHIP). */
  readOnly?: boolean;
  hint?: string;
}

/**
 * The canonical materials columns, in display order.
 *
 * VOCABULARY WARNING (contract §1, founder review point 4): `fabric_code` and
 * `color_code` here are PRODUCTION / VENDOR codes -- a company's internal
 * fabric code ("T-4821") or a vendor colour reference ("19-4052 TCX"). They
 * are NOT the style number's `fabric_class_code` / `color_class_code`, which
 * are 2-digit code_registry CLASSIFICATION values baked into the immutable
 * 9-segment identity and changeable only via Fork -> new UUID. The two are
 * disjoint vocabularies that happen to share short names. No panel control
 * may write a classification code into these fields; the Python model rejects
 * a bare 2-digit value here for exactly that reason.
 */
export const CANONICAL_MATERIAL_COLUMNS: MaterialColumn[] = [
  { field: "component",          label: "Component",       type: "text",     i18nKey: "col_component",   inStudioToday: true,  inSpecFlowToday: true  },
  { field: "material",           label: "Material",        type: "text",     i18nKey: "col_material",    inStudioToday: true,  inSpecFlowToday: true  },
  { field: "composition",        label: "Composition",     type: "text",     i18nKey: "col_composition", inStudioToday: true,  inSpecFlowToday: true  },
  { field: "gsm",                label: "GSM",             type: "text",     i18nKey: "col_gsm",         inStudioToday: true,  inSpecFlowToday: true  },
  { field: "fabric_code",        label: "Fabric Code",     type: "text",     i18nKey: "col_fabric_code", inStudioToday: true,  inSpecFlowToday: true,
    hint: "Internal/production fabric code, e.g. T-4821. NOT the style-number fabric class code." },
  { field: "vendor_article_no",  label: "Vendor Art. No.", type: "text",     i18nKey: null,              inStudioToday: false, inSpecFlowToday: false,
    hint: "The supplier's own article number, distinct from the internal fabric code." },
  { field: "color_code",         label: "Color Code",      type: "text",     i18nKey: "col_color",       inStudioToday: true,  inSpecFlowToday: true,
    hint: "Production/vendor colour reference. NOT the style-number colour class code." },
  { field: "width",              label: "Width",           type: "text",     i18nKey: "col_width",       inStudioToday: true,  inSpecFlowToday: false },
  { field: "specification",      label: "Specification",  type: "text",     i18nKey: null,              inStudioToday: true,  inSpecFlowToday: true  },
  { field: "consumption_qty",    label: "Consumption",     type: "number",   i18nKey: null,              inStudioToday: false, inSpecFlowToday: false },
  { field: "consumption_unit",   label: "Unit",            type: "text",     i18nKey: null,              inStudioToday: false, inSpecFlowToday: false,
    hint: "e.g. m, yd, pc" },
  { field: "unit_price",         label: "Unit Price",      type: "number",   i18nKey: "col_price",       inStudioToday: true,  inSpecFlowToday: false,
    hint: "Cost per unit of material. Studio's former bare 'price' maps here." },
  { field: "currency",           label: "Currency",        type: "currency", i18nKey: null,              inStudioToday: false, inSpecFlowToday: false,
    hint: "ISO 4217, e.g. USD" },
  { field: "estimated_line_cost", label: "Est. Line Cost", type: "number",   i18nKey: null,              inStudioToday: false, inSpecFlowToday: false,
    readOnly: true,
    hint: "unit_price x consumption_qty. Derived; blank unless BOTH inputs are present." },
  { field: "special_process_notes", label: "Special Process", type: "text",  i18nKey: null,              inStudioToday: false, inSpecFlowToday: false,
    hint: "Dye / wash / print / bonding etc." },
  { field: "swatch_uri",         label: "Swatch",          type: "image",    i18nKey: "col_swatch",      inStudioToday: true,  inSpecFlowToday: false,
    hint: "Studio stores this off-row today (images map, key bom_swatch_<rowid>); canonical puts it ON the row." },
];

/**
 * Costing is explicitly THREE fields, never collapsed into one
 * (contract §1, founder review point 3).
 *
 * `estimated_line_cost` is STORED, not recomputed on read, so a locked
 * Version's cost figure never silently drifts when unit_price is later edited
 * on some other pack sharing the same fabric. If either input is null the
 * line cost stays null -- never estimated from one input alone.
 */
export const COSTING_RELATIONSHIP = {
  inputs: ["unit_price", "consumption_qty"] as const,
  derived: "estimated_line_cost" as const,
  currencyField: "currency" as const,
};

/** Mirror of MaterialRow.recompute_line_cost(). Returns null unless BOTH
 *  inputs are real numbers -- never a partial estimate, never 0 as a stand-in
 *  for "unknown". */
export function computeLineCost(
  unitPrice: number | null | undefined,
  consumptionQty: number | null | undefined,
): number | null {
  if (unitPrice === null || unitPrice === undefined) return null;
  if (consumptionQty === null || consumptionQty === undefined) return null;
  if (Number.isNaN(unitPrice) || Number.isNaN(consumptionQty)) return null;
  return Math.round(unitPrice * consumptionQty * 1e6) / 1e6;
}

/** A bare 2-digit value in fabric_code/color_code is a code_registry
 *  CLASSIFICATION code written into a production field -- a category error.
 *  Mirrors assert_not_classification_code() in canonical_techpack.py. */
export function looksLikeClassificationCode(value: string | null | undefined): boolean {
  if (value === null || value === undefined) return false;
  return /^\d{2}$/.test(String(value).trim());
}

export const MATERIAL_SECTIONS = ["bom", "interlining", "trim"] as const;
export type MaterialSection = typeof MATERIAL_SECTIONS[number];

export const MATERIAL_SECTION_LABELS: Record<MaterialSection, string> = {
  bom: "Bill of Materials",
  interlining: "Interlining",
  trim: "Trim",
};

// ---------------------------------------------------------------------------
// Images -- 5 canonical slots (contract §1)
// ---------------------------------------------------------------------------

export const CANONICAL_IMAGE_SLOTS = [
  "design_sketch", "tech_sketch_front", "tech_sketch_back",
  "photo_front", "photo_back",
] as const;

export type CanonicalImageSlot = typeof CANONICAL_IMAGE_SLOTS[number];

export const IMAGE_SLOT_LABELS: Record<CanonicalImageSlot, string> = {
  design_sketch:     "Design Sketch",
  tech_sketch_front: "Technical Sketch — Front",
  tech_sketch_back:  "Technical Sketch — Back",
  photo_front:       "Photo — Front",
  photo_back:        "Photo — Back",
};

/**
 * Studio's LIVE Yjs image keys -> canonical slot names.
 *
 * FOUNDER DECISION 2026-09-17: live Yjs keys are NOT renamed in this pass.
 * Existing collaborative documents keep `tech_front`/`tech_back`; renaming
 * them would leave every already-saved document's images unresolvable unless
 * every live doc were rewritten, which is a CRDT migration this alignment
 * pass deliberately does not take on. The resolver translates instead.
 *
 * `design_sketch` has no legacy Studio key -- it is a NEW slot being added to
 * Studio's UI, so it maps to itself.
 */
export const STUDIO_IMAGE_KEY_TO_CANONICAL: Record<string, CanonicalImageSlot> = {
  tech_front:        "tech_sketch_front",
  tech_back:         "tech_sketch_back",
  photo_front:       "photo_front",
  photo_back:        "photo_back",
  design_sketch:     "design_sketch",
  // already-canonical spellings pass through unchanged
  tech_sketch_front: "tech_sketch_front",
  tech_sketch_back:  "tech_sketch_back",
};

/** Canonical slot -> the Yjs key Studio actually reads/writes. */
export const CANONICAL_TO_STUDIO_IMAGE_KEY: Record<CanonicalImageSlot, string> = {
  design_sketch:     "design_sketch",
  tech_sketch_front: "tech_front",
  tech_sketch_back:  "tech_back",
  photo_front:       "photo_front",
  photo_back:        "photo_back",
};

/** Studio's off-row swatch key for a materials row, e.g. bom_swatch_<rowid>.
 *  The resolver lifts these onto the canonical row's `swatch_uri`. */
export function studioSwatchKey(section: MaterialSection, rowId: string): string {
  return `${section}_swatch_${rowId}`;
}

// ---------------------------------------------------------------------------
// Canonical field paths (contract §0.3 -- what a revision event records)
// ---------------------------------------------------------------------------

/** Build the canonical dot-path a panel control writes to. Every control in
 *  either product must be able to produce one of these. */
export function materialPath(
  section: MaterialSection, rowIndex: number, field: string,
): string {
  return `${section}[${rowIndex}].${field}`;
}

export function measurementPath(key: CanonicalMeasurementKey): string {
  return `measurements.${key}`;
}

export function imagePath(slot: CanonicalImageSlot): string {
  return `images.${slot}`;
}

// ---------------------------------------------------------------------------
// Size presets -- SpecFlow has these, Studio does not (contract §1)
// ---------------------------------------------------------------------------

/**
 * `size_preset_ref` is METADATA ONLY: it records which preset a pack's
 * measurements were seeded from. It does NOT change how `measurements` is
 * stored, and selecting a preset never overwrites a value the user already
 * entered. Null means custom/manually entered.
 */
export const SIZE_PRESET_FIELD = "size_preset_ref";

export interface SizePresetOption {
  ref: string;
  label: string;
  category: "tops" | "bottoms" | "children" | "both";
}
